/**
 * Admin Invites Page
 * Admin panel for viewing all invites in the system
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Flex,
  Select,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Text,
  useToast,
  Spinner,
  HStack,
  VStack,
  Code,
  IconButton,
  Tooltip,
} from '@chakra-ui/react';
import { Copy, CheckCircle } from 'lucide-react';
import { format } from 'date-fns';
import {
  adminService,
  type AdminInvite,
  type AdminInviteListResponse,
  type GetInvitesParams,
} from '../../services/AdminService';

export const InvitesPage: React.FC = () => {
  const toast = useToast();

  // State
  const [invites, setInvites] = useState<AdminInvite[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 50,
    pages: 1,
  });

  // Filters
  const [statusFilter, setStatusFilter] = useState<'all' | 'available' | 'used'>('all');
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  // Load invites
  const loadInvites = async (params?: Partial<GetInvitesParams>) => {
    try {
      setLoading(true);
      const response = await adminService.getInvites({
        status: statusFilter,
        page: pagination.page,
        limit: pagination.limit,
        ...params,
      });

      setInvites(response.invites);
      setPagination(response.pagination);
    } catch (error: any) {
      toast({
        title: 'Error loading invites',
        description: error.response?.data?.detail || 'Failed to load invites',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    loadInvites();
  }, []);

  // Apply filter when it changes
  useEffect(() => {
    loadInvites({ page: 1 });
  }, [statusFilter]);

  // Handle pagination
  const handlePageChange = (newPage: number) => {
    setPagination({ ...pagination, page: newPage });
    loadInvites({ page: newPage });
  };

  // Copy invite code
  const handleCopyCode = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 2000);
      toast({
        title: 'Copied',
        description: 'Invite code copied to clipboard',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to copy code',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Format date
  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'MMM dd, yyyy HH:mm');
    } catch {
      return 'N/A';
    }
  };

  // Get status badge
  const getStatusBadge = (invite: AdminInvite) => {
    if (!invite.is_active) {
      return <Badge colorScheme="gray">Inactive</Badge>;
    }
    if (invite.used_at) {
      return <Badge colorScheme="green">Used</Badge>;
    }
    return <Badge colorScheme="blue">Available</Badge>;
  };

  return (
    <Box p={6}>
      <VStack spacing={6} align="stretch">

        {/* Filters */}
        <Box bg="white" p={6} borderRadius="md" shadow="sm">
          <HStack spacing={4}>
            {/* Status Filter */}
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as any)}
              flex={1}
              maxW="300px"
            >
              <option value="all">All Invites</option>
              <option value="available">Available</option>
              <option value="used">Used</option>
            </Select>
          </HStack>
        </Box>

        {/* Stats */}
        <HStack spacing={4}>
          <Box bg="white" p={4} borderRadius="md" shadow="sm" flex={1}>
            <Text fontSize="sm" color="gray.600">
              Total Invites
            </Text>
            <Text fontSize="2xl" fontWeight="bold">
              {pagination.total}
            </Text>
          </Box>
          <Box bg="white" p={4} borderRadius="md" shadow="sm" flex={1}>
            <Text fontSize="sm" color="gray.600">
              Available
            </Text>
            <Text fontSize="2xl" fontWeight="bold" color="blue.500">
              {invites.filter((i) => i.is_active && !i.used_at).length}
            </Text>
          </Box>
          <Box bg="white" p={4} borderRadius="md" shadow="sm" flex={1}>
            <Text fontSize="sm" color="gray.600">
              Used
            </Text>
            <Text fontSize="2xl" fontWeight="bold" color="green.500">
              {invites.filter((i) => i.used_at).length}
            </Text>
          </Box>
        </HStack>

        {/* Invites Table */}
        <Box bg="white" borderRadius="md" shadow="sm" overflow="hidden">
          {loading ? (
            <Flex justify="center" align="center" py={20}>
              <Spinner size="xl" color="blue.500" />
            </Flex>
          ) : invites.length === 0 ? (
            <Flex justify="center" align="center" py={20}>
              <Text fontSize="lg" color="gray.500">
                No invites found
              </Text>
            </Flex>
          ) : (
            <>
              <Table variant="simple">
                <Thead bg="gray.50">
                  <Tr>
                    <Th>Code</Th>
                    <Th>Status</Th>
                    <Th>Created By</Th>
                    <Th>Created At</Th>
                    <Th>Used By</Th>
                    <Th>Used At</Th>
                    <Th>Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {invites.map((invite) => (
                    <Tr key={invite.id} _hover={{ bg: 'gray.50' }}>
                      {/* Code */}
                      <Td>
                        <HStack spacing={2}>
                          <Code fontSize="sm" colorScheme="blue">
                            {invite.code}
                          </Code>
                          <Tooltip label={copiedCode === invite.code ? 'Copied!' : 'Copy code'}>
                            <IconButton
                              aria-label="Copy code"
                              icon={copiedCode === invite.code ? <CheckCircle size={14} /> : <Copy size={14} />}
                              size="xs"
                              variant="ghost"
                              onClick={() => handleCopyCode(invite.code)}
                              colorScheme={copiedCode === invite.code ? 'green' : 'gray'}
                            />
                          </Tooltip>
                        </HStack>
                      </Td>

                      {/* Status */}
                      <Td>{getStatusBadge(invite)}</Td>

                      {/* Created By */}
                      <Td>
                        <Text fontSize="sm" color="gray.600">
                          {invite.created_by?.username || '—'}
                        </Text>
                      </Td>

                      {/* Created At */}
                      <Td>
                        <Text fontSize="sm" color="gray.600">
                          {formatDate(invite.created_at)}
                        </Text>
                      </Td>

                      {/* Used By */}
                      <Td>
                        {invite.used_by ? (
                          <HStack spacing={2}>
                            <Text fontSize="sm" fontWeight="medium">
                              {invite.used_by.username}
                            </Text>
                          </HStack>
                        ) : (
                          <Text fontSize="sm" color="gray.400">
                            —
                          </Text>
                        )}
                      </Td>

                      {/* Used At */}
                      <Td>
                        <Text fontSize="sm" color="gray.600">
                          {invite.used_at ? formatDate(invite.used_at) : '—'}
                        </Text>
                      </Td>

                      {/* Actions */}
                      <Td>
                        <Text fontSize="xs" color="gray.400">
                          View only
                        </Text>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>

              {/* Pagination */}
              {pagination.pages > 1 && (
                <Flex justify="space-between" align="center" p={4} borderTop="1px" borderColor="gray.200">
                  <Text fontSize="sm" color="gray.600">
                    Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
                    {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} invites
                  </Text>
                  <HStack spacing={2}>
                    <Button
                      size="sm"
                      onClick={() => handlePageChange(pagination.page - 1)}
                      isDisabled={pagination.page === 1}
                    >
                      Previous
                    </Button>
                    {Array.from({ length: Math.min(pagination.pages, 5) }, (_, i) => {
                      const pageNum = i + 1;
                      return (
                        <Button
                          key={pageNum}
                          size="sm"
                          variant={pagination.page === pageNum ? 'solid' : 'outline'}
                          colorScheme={pagination.page === pageNum ? 'blue' : 'gray'}
                          onClick={() => handlePageChange(pageNum)}
                        >
                          {pageNum}
                        </Button>
                      );
                    })}
                    <Button
                      size="sm"
                      onClick={() => handlePageChange(pagination.page + 1)}
                      isDisabled={pagination.page === pagination.pages}
                    >
                      Next
                    </Button>
                  </HStack>
                </Flex>
              )}
            </>
          )}
        </Box>
      </VStack>
    </Box>
  );
};

export default InvitesPage;
